// Captures a promo code arriving via URL (?promo=NESSLABS) so it can prefill the
// "Have a promo code?" input in SubscriptionModal after signup. Deliberately kept
// OUT of the signup-attribution string (signupReferrer.js): a promo code is not an
// acquisition source — campaign attribution rides on utm_source (e.g. nesslabs).
// Per the attribution-capture lesson, a param that can arrive alone needs its own
// capture branch — this module is that branch.
//
// localStorage (not sessionStorage) on purpose: readers often sign up in a fresh
// tab or come back later; the code should survive until it is redeemed or replaced.
const PROMO_CODE_KEY = 'linkflare_promo_code'
const MAX_CODE_LENGTH = 50

// Storage can be unavailable (private browsing, "Block All Cookies") — keep the captured code
// in memory too so the prefill still works within the page session that saw the URL.
let memoryCode = null

// Keep in sync with webknox PromoCodes.normalizeCode (trim + Locale.ROOT uppercase + length cap
// + the [A-Z0-9_-] charset, which the backend admin-create endpoint enforces too).
export function normalizePromoCode(value) {
  if (typeof value !== 'string') return null
  const code = value.trim().toUpperCase()
  if (!code || code.length > MAX_CODE_LENGTH) return null
  if (!/^[A-Z0-9_-]+$/.test(code)) return null
  return code
}

/** Store a ?promo= code if present; returns the effective stored code (new or prior), or null. */
export function capturePromoCode() {
  let code = null
  try {
    code = normalizePromoCode(new URLSearchParams(window.location.search).get('promo'))
  } catch (e) {
    // no usable URL — fall through to whatever is stored
  }
  if (code) {
    memoryCode = code
    try {
      localStorage.setItem(PROMO_CODE_KEY, code)
    } catch (e) {
      // Private browsing / storage disabled — memoryCode carries the session.
    }
    return code
  }
  return peekPromoCode()
}

export function peekPromoCode() {
  try {
    const stored = normalizePromoCode(localStorage.getItem(PROMO_CODE_KEY))
    if (stored) return stored
  } catch (e) {
    // storage unavailable — fall through to memory
  }
  return memoryCode
}

export function clearPromoCode() {
  memoryCode = null
  try {
    localStorage.removeItem(PROMO_CODE_KEY)
  } catch (e) {
    // best-effort
  }
}
